import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import { toast } from 'react-toastify';
import { FiDollarSign, FiCheck } from 'react-icons/fi';

const CURRENCIES = [
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  // { code: 'EUR', symbol: '€', name: 'Euro' },
  // { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  // { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  // { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  // { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong' },
  // { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  // { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
  // { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  // { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  // { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
];

const Settings = () => {
  const { user } = useAuth();
  const isReseller = user?.role === 'reseller';

  const currentCurrency = user?.resellerId?.currency || { code: 'MYR', symbol: 'RM' };
  const [currency, setCurrency] = useState(currentCurrency.code);
  const [saving, setSaving] = useState(false);

  const handleCurrencyChange = async (code) => {
    const selected = CURRENCIES.find((c) => c.code === code);
    if (!selected) return;

    setCurrency(code);
    setSaving(true);
    try {
      await authService.updateCurrency(selected.code, selected.symbol);
      toast.success(`Currency updated to ${selected.code} (${selected.symbol})`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update currency');
      setCurrency(currentCurrency.code);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-0.5">Manage your account and preferences</p>
      </div>

      {/* Profile */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">Profile Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Full Name</p>
            <p className="text-sm font-medium text-gray-900">{user?.fullName}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Email</p>
            <p className="text-sm font-medium text-gray-900">{user?.email}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Role</p>
            <p className="text-sm font-medium text-gray-900 capitalize">{user?.role}</p>
          </div>
        </div>
      </div>

      {/* Currency — only for resellers */}
      {isReseller && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-1">
            <FiDollarSign size={15} className="text-purple-600" />
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Default Currency</h2>
          </div>
          <p className="text-xs text-gray-400 mb-4">This currency will be used for plan pricing and merchant billing</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {CURRENCIES.map((c) => {
              const isActive = currency === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  disabled={saving}
                  onClick={() => handleCurrencyChange(c.code)}
                  className={`relative flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-all ${
                    isActive
                      ? 'border-purple-400 bg-purple-50 ring-1 ring-purple-300'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  } ${saving ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`text-base font-semibold ${isActive ? 'text-purple-700' : 'text-gray-600'}`}>
                    {c.symbol}
                  </span>
                  <div className="min-w-0">
                    <p className={`text-xs font-semibold leading-tight ${isActive ? 'text-purple-800' : 'text-gray-800'}`}>{c.code}</p>
                    <p className="text-[10px] text-gray-400 truncate">{c.name}</p>
                  </div>
                  {isActive && (
                    <FiCheck size={13} className="absolute top-1.5 right-1.5 text-purple-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
